import { useState } from "react";
import { Module } from "@/constants";
import { useCachedQuery } from "@/hooks/useCachedQuery";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ContractorPanelPlannedHoursAccept } from "@/types/contractorPlannedHours";
import { ContractorPanelPlannedHoursService } from "../services/contractorPanelPlannedHoursService";
import { useContractorId } from "@/modules/account/hooks/useContractorId";

export const useContractorPanelPlannedHours = () => {
  const contractorId = useContractorId();
  const [acceptedPlannedHours, setAcceptedPlannedHours] =
    useState<ContractorPanelPlannedHoursAccept | null>(null);

  const statusQuery = useCachedQuery({
    queryKey: [Module.ContractorPanelPlannedHours, "status", contractorId],
    queryFn: () => ContractorPanelPlannedHoursService.getStatus(),
    staleTime: 0,
  });

  const acceptMutation = useMutationWithErrorHandling({
    mutationFn: () => ContractorPanelPlannedHoursService.accept(),
    onSuccess: (data) => {
      setAcceptedPlannedHours(data);
    },
  });

  const showHours = () => {
    acceptMutation.mutate(undefined);
  };

  const shouldShowPlannedHours = statusQuery.data?.should_show_planned_hours;
  const hasAcceptedPlannedHours = Boolean(acceptedPlannedHours);

  const canContractorAcceptPlannedHours =
    shouldShowPlannedHours === true && !hasAcceptedPlannedHours;
  const shouldShowPlannedHoursNoChangesMessage =
    shouldShowPlannedHours === false && !hasAcceptedPlannedHours;

  const isBusy =
    statusQuery.isLoading || statusQuery.isFetching || acceptMutation.isPending;

  return {
    acceptedPlannedHours,
    isBusy,
    isSubmitting: acceptMutation.isPending,
    shouldShowPlannedHoursButton: canContractorAcceptPlannedHours,
    shouldShowNoChangesMessage: shouldShowPlannedHoursNoChangesMessage,
    showHours,
  };
};
